/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { zIndex } from '../../components/css'
import { Flex } from '../../components/Layout'
import Text from 'src/components/ts-components/Text'
import { Progress } from 'antd'
import ReprocessingIcon from 'src/components/ts-components/icons/ReprocessingIcon'
import colors from '@sportninja/common/constants/appColors'
import { CloseCircleOutlined, CloseOutlined } from '@ant-design/icons'

const ReprocessingSettingsOverlay = ({ scheduleReprocessingListener }) => {
  const handleClose = useCallback(() => {
    window.location.reload()
  }, [])

  if (!scheduleReprocessingListener) {
    return false
  }

  return (
    <Flex
      column
      alignItems='center'
      css={css`
        position: fixed;
        top: calc(100vh - 100%);
        left: 0;
        right: 0;
        bottom: 0;
        z-index: ${zIndex.base};
        /* fill the screen with a blur overlay */
        background-color: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(2px);
      `}
    >
      <Flex
        column
        alignItems='center'
        noFlex
        css={css`
          /* Modal container styles aligned with Figma */
          background-color: #1b222b;
          border: 1px solid #27303e;
          border-radius: 16px;
          padding: 24px;
          gap: 8px;
          width: 500px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `}
      >
        {/* Close button - only when error is present */}
        {scheduleReprocessingListener?.errorMessage ? (
          <button
            onClick={() => handleClose()}
            aria-label='Close overlay'
            css={css`
              position: absolute;
              top: 12px;
              right: 12px;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: transparent;
              border: none;
              color: ${colors.NEUTRAL_0};
              cursor: pointer;
              border-radius: 8px;
            `}
          >
            <CloseOutlined />
          </button>
        ) : null}

        {/* Icon */}
        {scheduleReprocessingListener?.errorMessage ? (
          <CloseCircleOutlined
            style={{ fontSize: 56, color: colors.ERROR_200 }}
          />
        ) : (
          <ReprocessingIcon size={56} />
        )}
        {/* Title */}
        {scheduleReprocessingListener?.errorMessage ? (
          <Text
            variant='heading3'
            weight='semiBold'
            style={
              'text-align: center; color: #ffffff; text-transform: capitalize;'
            }
          >
            Reprocessing Error
          </Text>
        ) : (
          <Text
            variant='heading3'
            weight='semiBold'
            style={
              'text-align: center; color: #ffffff; text-transform: capitalize;'
            }
          >
            Reprocessing Statistics
          </Text>
        )}
        {/* Description */}
        {!scheduleReprocessingListener?.errorMessage ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
            style={'text-align: center; text-transform: capitalize;'}
          >
            You can safely leave this page — standings and stats will update
            once reprocessing is complete.
          </Text>
        ) : null}
        {scheduleReprocessingListener?.errorMessage ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.ERROR_0}
            style={'margin-top: 8px; text-align: center;'}
          >
            {scheduleReprocessingListener?.errorMessage ||
              'An Unknown Error Occurred, Try Again Later.'}
          </Text>
        ) : (
          <>
            {/* Progress bar */}
            <div
              css={css`
                position: relative;
                width: 100%;
                margin-top: 8px;
              `}
            >
              <Progress
                percent={scheduleReprocessingListener?.progress || 0}
                showInfo={false}
                strokeColor={'#38afff'}
                trailColor={'rgba(120, 120, 120, 0.2)'}
                strokeWidth={24}
              />
              <div
                css={css`
                  position: absolute;
                  left: 0;
                  right: 0;
                  top: 0;
                  bottom: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #ffffff;
                  pointer-events: none;
                `}
              >
                {scheduleReprocessingListener?.progress || 0}%
              </div>
            </div>

            {typeof scheduleReprocessingListener?.count === 'number' && (
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
                style={'margin-top: 8px; text-align: center;'}
              >
                Processing {scheduleReprocessingListener.count} games
              </Text>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default ReprocessingSettingsOverlay
